import { ArrowClockwise, ArrowLeft, Construction, CopySimple, Lock, PlugZap, ShieldAlert, WarningOctagon } from '@/design-system/icons'
import { useEffect } from 'react'
import { isRouteErrorResponse, Link, useLocation, useRouteError } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, notify, Page, PageHeader } from '@/design-system'
import { playAlertSound } from '@/design-system/sound'
import { readError } from '@/design-system/errorReason'
import { useUi } from '@/stores/ui'
import { useT } from '@/i18n'
import { GAPS } from '@/navigation/gaps'
import { flattenPages } from '@/navigation/tree'
import type { NavPage } from '@/navigation/types'
import { useAuth } from '@/stores/auth'

/**
 * react-router `errorElement` for the app shell: a page that throws (render error, loader error, unhandled
 * exception) replaces only that page's content with this screen — the sidebar and topbar stay usable so
 * Operations can navigate away — states plainly that something broke, why, and lets them retry or report it.
 */
export function RouteErrorBoundary() {
  const error = useRouteError()
  const { t, locale } = useT()
  const ar = locale === 'ar'
  const location = useLocation()
  const message = error instanceof ApiError || error instanceof Error ? error.message : isRouteErrorResponse(error) ? error.statusText : String(error)
  const requestId = error instanceof ApiError ? error.requestId : undefined
  // The same plain-Arabic reading the toast card uses, so a crashed page explains itself the same way.
  const reading = readError(error)

  useEffect(() => {
    playAlertSound({ enabled: useUi.getState().errorSound })
  }, [location.pathname])

  return (
    <Page>
      <EmptyState
        icon={WarningOctagon}
        title={ar ? reading.title : 'This page ran into an unexpected error'}
        description={
          <span className="block max-w-md space-y-1">
            {ar && <span className="block text-sm text-fg">{reading.reason}</span>}
            <span className="block font-mono text-xs" dir="ltr">{message}</span>
            {requestId && <span className="block text-2xs text-fg-subtle">{t('error.requestId', { id: requestId })}</span>}
          </span>
        }
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button icon={ArrowClockwise} variant="primary" onClick={() => window.location.reload()}>{t('common.retry')}</Button>
            <Link to="/"><Button icon={ArrowLeft}>{t('shell.home')}</Button></Link>
            <Button icon={CopySimple} variant="ghost" onClick={() => { void navigator.clipboard.writeText(`${message}${requestId ? ` (Ref ${requestId})` : ''}`); notify.success(ar ? 'تم النسخ' : 'Copied') }}>{ar ? 'نسخ التفاصيل' : 'Copy details'}</Button>
          </div>
        }
      />
    </Page>
  )
}

export function NotFoundPage() {
  const { t } = useT()
  return (
    <Page>
      <EmptyState title={t('error.notFound')} action={<Link to="/"><Button icon={ArrowLeft}>{t('shell.home')}</Button></Link>} />
    </Page>
  )
}

export function ForbiddenPage({ permission }: { permission?: string }) {
  const { t } = useT()
  return (
    <Page>
      <EmptyState icon={Lock} title={t('error.forbidden')} description={permission ? t('error.forbiddenHint', { permission }) : undefined} />
    </Page>
  )
}

export function NoAccessPage() {
  const { t } = useT()
  const user = useAuth((s) => s.user)
  return (
    <Page>
      <EmptyState icon={ShieldAlert} title={t('auth.noPermissions')} description={t('auth.noPermissionsHint', { user: user?.username ?? '', role: String(user?.vsoftRoleId ?? '—') })} />
    </Page>
  )
}

function RelatedLinks({ ids }: { ids?: string[] }) {
  const { l } = useT()
  const can = useAuth((s) => s.can)
  const pages = flattenPages().filter(({ page }) => ids?.includes(page.id) && can(page.permission))
  if (!pages.length) return null
  return (
    <div className="flex flex-wrap gap-2">
      {pages.map(({ page }) => (
        <Link key={page.id} to={page.path}>
          <Button size="sm">{l(page.label)}</Button>
        </Link>
      ))}
    </div>
  )
}

/** Rendered for navigation entries with no Dispatch endpoint. Shows the documented gap — never fake data. */
export function IntegrationGapPage({ page }: { page: NavPage }) {
  const { t, l } = useT()
  const gap = page.gap ? GAPS[page.gap] : undefined
  return (
    <Page>
      <PageHeader title={l(page.label)} meta={<Badge tone="warning" dot>{t('gap.title')}</Badge>} />
      <Card>
        <CardBody className="flex flex-col gap-5">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-warning-soft text-warning">
              <PlugZap size={20} aria-hidden />
            </span>
            <div>
              <p className="font-medium">{gap ? l(gap.feature) : l(page.label)}</p>
              <p className="mt-1 text-sm text-fg-muted">{t('gap.body')}</p>
              {gap && <p className="mt-2 text-sm text-fg-muted">{gap.current}</p>}
            </div>
          </div>
          {gap && (
            <>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('gap.required')}</p>
                <ul className="space-y-1.5">
                  {gap.required.map((r) => (
                    <li key={r} className="rounded-md bg-bg-subtle px-3 py-2 font-mono text-xs">{r}</li>
                  ))}
                </ul>
              </div>
              {gap.related?.length ? (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('gap.related')}</p>
                  <RelatedLinks ids={gap.related} />
                </div>
              ) : null}
              <p className="text-xs text-fg-subtle">{t('gap.reference')}: {gap.id} — docs/INTEGRATION_GAPS.md</p>
            </>
          )}
        </CardBody>
      </Card>
    </Page>
  )
}

/** Screens whose Dispatch endpoints are mapped and whose UI is scheduled in the roadmap. */
export function ScheduledPage({ page }: { page: NavPage }) {
  const { l, locale } = useT()
  const gap = page.gap ? GAPS[page.gap] : undefined
  return (
    <Page>
      <PageHeader
        title={l(page.label)}
        meta={<Badge tone="info" dot>{locale === 'ar' ? 'واجهة API مربوطة — الشاشة قيد التنفيذ' : 'API mapped — screen in progress'}</Badge>}
      />
      <Card>
        <CardHeader title={locale === 'ar' ? 'الربط مع Dispatch API' : 'Dispatch API integration'} description={locale === 'ar' ? 'نقاط الاتصال (Endpoints) التي ستستخدمها هذه الشاشة (تم التحقق منها مقابل Swagger).' : 'Endpoints this screen uses (verified against the Swagger contract).'} />
        <CardBody className="space-y-4">
          <div className="flex items-start gap-3 text-sm text-fg-muted">
            <Construction size={18} className="mt-0.5 shrink-0 text-info" aria-hidden />
            {locale === 'ar' ? 'هذه الشاشة ضمن المرحلة التالية من التنفيذ. لا تعرض أي بيانات تجريبية.' : 'This screen is part of the next delivery phase. No sample data is shown.'}
          </div>
          <ul className="grid gap-1.5">
            {page.api?.map((e) => {
              const [method, path] = e.split(' ')
              return (
                <li key={e} className="flex items-center gap-2 rounded-md bg-bg-subtle px-3 py-2 font-mono text-xs">
                  <Badge tone={method === 'GET' ? 'success' : method === 'DELETE' ? 'danger' : method === 'PUT' ? 'warning' : 'info'} className="w-14 justify-center">{method}</Badge>
                  <span className="truncate" dir="ltr">{path}</span>
                </li>
              )
            })}
          </ul>
          {gap && <p className="text-xs text-warning">{locale === 'ar' ? 'جزء من الشاشة يتطلب Backend إضافيا' : 'Part of this screen needs additional backend'}: {gap.id} — {l(gap.feature)}</p>}
        </CardBody>
      </Card>
    </Page>
  )
}
